import { useRef, useEffect, useState, useCallback } from 'react';

/**
 * Provides nodes to observe and intersection entries.
 * Used by useViewEvents to fire tagular events when elements enter the viewport.
 *
 * Returns:
 * - `setNodes` — set the elements to observe (replaces the current set).
 * - `entries` — latest IntersectionObserver entries (most recent batch).
 * - `disconnect` — explicitly tear down the observer. Idempotent. Use
 *   this from a fire-once consumer that wants to permanently stop
 *   observing after the first intersect, instead of calling
 *   `setNodes([])` which still spins up a fresh (empty) observer on
 *   the next render. Especially relevant under React 18 Strict Mode
 *   where the cleanup → setup cycle can otherwise re-attach an
 *   observer and re-fire entries on the same node (EDUN-10692 bug
 *   fix companion).
 *
 * @param threshold - IntersectionObserver threshold (0–1). Default 1 = fully visible.
 */
export const useIntersect = (threshold: number = 1) => {
  const [entries, setEntries] = useState<IntersectionObserverEntry[]>([]);
  const [nodes, setNodes] = useState<Element[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);
  const disconnectedRef = useRef(false);

  const disconnect = useCallback(() => {
    disconnectedRef.current = true;
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
  }, []);

  useEffect(() => {
    if (disconnectedRef.current) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
    if (nodes.length === 0) {
      return;
    }

    // Create the observer in the realm of the observed node, not blindly from
    // the parent `window`. In production the node lives in the top-level
    // document so this resolves to `window` (no behavior change). In the dev
    // harness's viewport simulator the experience — and any portaled drawer —
    // is mounted inside an <iframe>; an observer created from the parent
    // `window` with `root: null` resolves to the parent viewport and cannot
    // observe the iframe's (fixed-positioned) nodes, so view events would
    // silently never fire. Resolving the realm from the node's own document
    // keeps `root: null` pointed at the correct viewport in both contexts.
    const observerView = nodes[0]?.ownerDocument?.defaultView ?? window;
    const ObserverCtor =
      observerView.IntersectionObserver ?? window.IntersectionObserver;
    if (typeof ObserverCtor !== 'function') {
      return;
    }

    observer.current = new ObserverCtor(
      (newEntries) => setEntries(newEntries),
      { root: null, rootMargin: '0px', threshold },
    );
    nodes.forEach((node) => observer.current?.observe(node));
    return () => observer.current?.disconnect();
  }, [nodes, threshold]);

  return { setNodes, entries, disconnect };
};
